import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';

export interface DimensionalViewRow {
  id: string;
  path: string[];
  [key: string]: unknown;
}

export interface RowMemberOption extends AutoCompleteItem {
  value: string;
  level: number;
}

export const ALL_ROW_MEMBERS = '__all_row_members__';

// Down options whose rows are KTree members (a parent/child hierarchy), as opposed to measures or periods
const MEMBER_DIMENSION_TYPES = ['MAIN_MEMBER', 'SECOND_MEMBER'];

export function isMemberDimension(dimensionType?: string) {
  return !!dimensionType && MEMBER_DIMENSION_TYPES.includes(dimensionType);
}

const PATH_SEPARATOR = '\u0000';

// Lists the row members depth-first, in the order the tree grid shows them.
// Trees with several top members get an "All" option so the user can get back to every row.
export function getRowMemberFilterOptions(rows: DimensionalViewRow[]): RowMemberOption[] {
  const childrenByParentPath = new Map<string, DimensionalViewRow[]>();
  rows.forEach((row) => {
    const parentPath = row.path.slice(0, -1).join(PATH_SEPARATOR);
    childrenByParentPath.set(parentPath, [...(childrenByParentPath.get(parentPath) ?? []), row]);
  });

  const rootRows = childrenByParentPath.get('') ?? [];
  const rootLevel = rootRows.length > 1 ? 1 : 0;

  const collectOptions = (parentPath: string, level: number): RowMemberOption[] =>
    (childrenByParentPath.get(parentPath) ?? []).flatMap((row) => [
      { value: row.id, label: row.path[row.path.length - 1], level },
      ...collectOptions(row.path.join(PATH_SEPARATOR), level + 1),
    ]);

  const memberOptions = collectOptions('', rootLevel);
  if (rootLevel === 0) return memberOptions;

  return [{ value: ALL_ROW_MEMBERS, label: 'All', level: 0 }, ...memberOptions];
}

// Keeps the selected member and everything under it, with the member as the top of the tree
export function filterRowsByMember<T extends DimensionalViewRow>(rows: T[], memberId: string | null): T[] {
  const memberPath = rows.find((row) => row.id === memberId)?.path;
  if (!memberPath) return rows;

  const depth = memberPath.length - 1;
  return rows
    .filter((row) => memberPath.every((pathItem, index) => row.path[index] === pathItem))
    .map((row) => (depth === 0 ? row : { ...row, path: row.path.slice(depth) }));
}
